import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, getUserId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COLORS } from "@/data/constants";
import { toast } from "sonner";
import { ArrowLeft, Lock, RotateCcw, Backpack, ArrowRight, BookOpen, Sparkles } from "lucide-react";

const C_META = [
  { key: "corps", label: "CORPS", color: COLORS.vert },
  { key: "coeur", label: "CŒUR", color: COLORS.rouge },
  { key: "cerveau", label: "CERVEAU", color: COLORS.bleu },
  { key: "conscience", label: "CONSCIENCE", color: COLORS.or },
];
const EMPTY_SCORES = { corps: 0, coeur: 0, cerveau: 0, conscience: 0 };
const IMG_BASE = "https://static.prod-images.emergentagent.com/jobs/0076e22e-a00f-4e60-8a47-33baa02e8fb0/images/";
const OBJECT_IMAGES = {
  "Pièce d'or": IMG_BASE + "e0197a4a8e72d10e9d4b8ca0c6ca076ff1da8e3e6148271d2fd7ac86fdc5cfaf.jpeg",
  "Boussole": IMG_BASE + "b2d5a225b0115fdf9e56dff33ef19f8583b00be807de86d800f3b53e4ba61157.jpeg",
  "Clé noire": IMG_BASE + "93ab036564041199655904fd6bf9b9ac0ea2a1a7e755188040917007752b2e59.jpeg",
  "Miroir": IMG_BASE + "171ceaf468b2ac87c02f67ff54f702c2d163f441b833ed48009ab1c76ede1944.jpeg",
  "Plume": IMG_BASE + "305f5ba24dcfe08a56b94394c4f6765a551d95bc16ed2471ab007ecf252c74cb.jpeg",
  "Pierre rouge": IMG_BASE + "422c0c11145ba04e1b96847fae3a87feafd151a804eb8d8175681cfe915c44f7.jpeg",
};
const ENDING_LABELS = { "FIN-TRESOR": "Trésor", "FIN-JARDIN": "Jardin", "FIN-SOURCE": "Source", "FIN-CHEMIN": "Chemin", "FIN-PORTE": "Porte", "J21-SECRET": "Secret" };
const ENDING_CONCLUSIONS = {
  "FIN-TRESOR": "Le vrai trésor, c'était vous.",
  "FIN-JARDIN": "Votre abondance grandit lorsqu'elle se partage.",
  "FIN-SOURCE": "La source coule désormais en vous.",
  "FIN-CHEMIN": "Le chemin lui-même était la récompense.",
  "FIN-PORTE": "Une nouvelle étape de vie s'ouvre à vous.",
  "J21-SECRET": "Vous avez trouvé ce que peu de voyageurs découvrent.",
};

export default function AdventureReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user ? user.user_id : getUserId();

  const [book, setBook] = useState(null);
  const [state, setState] = useState(null);
  const [scene, setScene] = useState(null);
  const [entitled, setEntitled] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [view, setView] = useState("loading"); // loading | scene | freeEnd | lock | ending
  const [journalText, setJournalText] = useState("");
  const [reflection, setReflection] = useState("");
  const [reflecting, setReflecting] = useState(false);

  const persist = useCallback((next) => {
    setState(next);
    api.post("/adventure/state", { user_id: userId, book_id: id, ...next }).catch(() => {});
  }, [userId, id]);

  const loadScene = useCallback(async (sceneId, curState) => {
    try {
      const { data } = await api.get(`/adventure/scene/${id}/${sceneId}`);
      setScene(data);
      setJournalText((curState?.journal && curState.journal[sceneId]) || "");
      if ((data.choices || []).length === 0) setView("ending");
      else setView("scene");
    } catch (err) {
      if (err.response?.status === 403) {
        setView(curState?.step >= 3 && curState?.step < 4 ? "freeEnd" : "lock");
        // if just finished step 3 but current scene is premium (step will be 4 after we set current_scene)
      } else {
        toast.error("Scène introuvable");
      }
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      setView("loading");
      const [bRes, eRes, sRes] = await Promise.all([
        api.get(`/adventure/books/${id}`),
        api.get(`/adventure/entitlement/${id}`),
        api.get(`/adventure/state/${userId}/${id}`),
      ]);
      setBook(bRes.data);
      setEntitled(eRes.data.entitled);
      setAuthenticated(eRes.data.authenticated);
      const st = { ...sRes.data, scores: { ...EMPTY_SCORES, ...(sRes.data.scores || {}) } };
      setState(st);
      await loadScene(st.current_scene, st);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userId]);

  const hasObject = (obj) => !obj || (state?.inventory || []).includes(obj);

  const chooseJournalSave = () => {
    if (!scene?.journalPrompt) return;
    const next = { ...state, journal: { ...(state.journal || {}), [scene.scene_id]: journalText } };
    persist(next);
    toast.success("Noté dans votre carnet");
  };

  const askReflection = async () => {
    if (!journalText.trim()) { toast.error("Écrivez d'abord quelques mots"); return; }
    setReflecting(true); setReflection("");
    try {
      const { data } = await api.post("/adventure/reflect", {
        prompt: scene.journalPrompt, response: journalText,
        scene_title: scene.title, session_id: `${userId}_${scene.scene_id}`,
      });
      setReflection(data.reflection);
    } catch {
      toast.error("Le compagnon n'a pas pu répondre pour l'instant");
    } finally {
      setReflecting(false);
    }
  };

  const makeChoice = async (choice) => {
    if (!hasObject(choice.requiredObject)) {
      toast.error(`Nécessite : ${choice.requiredObject}`);
      return;
    }
    const scores = { ...state.scores };
    Object.entries(choice.C_effect || {}).forEach(([k, v]) => { scores[k] = (scores[k] || 0) + v; });
    const inventory = [...(state.inventory || [])];
    if (choice.objectReward && !inventory.includes(choice.objectReward)) inventory.push(choice.objectReward);
    const journal = { ...(state.journal || {}) };
    if (scene.journalPrompt && journalText.trim()) journal[scene.scene_id] = journalText;
    const visited = [...new Set([...(state.visited || []), scene.scene_id])];
    const next = {
      ...state, scores, inventory, journal, visited,
      current_scene: choice.destination,
      step: Math.max(state.step || 1, (scene.step || 1)),
    };
    persist(next);
    await loadScene(choice.destination, next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startCheckout = async () => {
    if (!authenticated) {
      sessionStorage.setItem("tao_unlock_book", id);
      navigate(`/connexion?next=/aventure/${id}`);
      return;
    }
    sessionStorage.setItem("tao_unlock_book", id);
    try {
      const { data } = await api.post("/payments/checkout", { book_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      if (err.response?.status === 400) { toast.success("Aventure déjà débloquée"); window.location.reload(); }
      else toast.error("Impossible de démarrer le paiement");
    }
  };

  const startPaypal = async () => {
    if (!authenticated) {
      sessionStorage.setItem("tao_unlock_book", id);
      navigate(`/connexion?next=/aventure/${id}`);
      return;
    }
    sessionStorage.setItem("tao_unlock_book", id);
    try {
      const { data } = await api.post("/paypal/create-order", { book_id: id, origin_url: window.location.origin });
      if (data.approve_url) window.location.href = data.approve_url;
      else toast.error("PayPal indisponible");
    } catch (err) {
      if (err.response?.status === 400) { toast.success("Aventure déjà débloquée"); window.location.reload(); }
      else toast.error("PayPal indisponible pour le moment");
    }
  };

  const restart = async () => {
    const { data } = await api.post("/adventure/reset", { user_id: userId, book_id: id, current_scene: "J01" });
    const st = { ...data, scores: { ...EMPTY_SCORES, ...(data.scores || {}) } };
    setState(st);
    await loadScene(st.current_scene, st);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (view === "loading" || !book || !state) {
    return <div className="max-w-3xl mx-auto px-4 py-24 text-center font-sans text-[#F6F2E9]/60">Chargement…</div>;
  }

  return (
    <div className="min-h-screen bg-[#1F2A24] text-[#F6F2E9]">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 md:py-12">
        <Link to="/aventures" data-testid="adventure-back" className="inline-flex items-center gap-2 font-sans text-sm text-[#F6F2E9]/50 hover:text-[#C9A24B] transition-colors">
          <ArrowLeft size={16} /> Les aventures
        </Link>

        {/* progress */}
        <div className="mt-5">
          <div className="flex justify-between font-sans text-xs tracking-[0.2em] text-[#C9A24B]">
            <span data-testid="adventure-step">ÉTAPE {scene?.step || state.step} / {book.total_steps}</span>
            <span>{book.title}</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 mt-2 overflow-hidden">
            <div className="h-full bg-[#C9A24B] transition-all duration-500" style={{ width: `${Math.round(((scene?.step || state.step) / book.total_steps) * 100)}%` }} />
          </div>
        </div>

        <ScoreBar scores={state.scores} />
        <Inventory items={state.inventory || []} />

        {view === "scene" && scene && (
          <div data-testid="adventure-scene" className="mt-8">
            {scene.character && (
              <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#C9A24B]/80">{scene.character}</p>
            )}
            <h1 data-testid="adventure-scene-title" className="font-serif text-3xl md:text-4xl mt-2">{scene.title}</h1>
            <div className="gold-divider w-16 my-5" />
            <p className="font-serif text-lg md:text-xl leading-relaxed text-[#F6F2E9]/90 whitespace-pre-line">{scene.text}</p>

            {scene.journalPrompt && (
              <div className="mt-7 rounded-2xl border border-[#C9A24B]/40 bg-white/5 p-5">
                <p className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-[#C9A24B] font-semibold">
                  <BookOpen size={14} /> Mon carnet
                </p>
                <p className="font-serif italic text-[#F6F2E9]/85 mt-2">{scene.journalPrompt}</p>
                <textarea
                  data-testid="hero-journal-input"
                  value={journalText}
                  onChange={(e) => setJournalText(e.target.value)}
                  onBlur={chooseJournalSave}
                  rows={3}
                  placeholder="Écrivez ce qui vous vient…"
                  className="w-full mt-3 rounded-xl bg-[#F6F2E9] text-[#1F2A24] p-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]"
                />
                <div className="mt-3">
                  <button
                    data-testid="reflect-button"
                    onClick={askReflection}
                    disabled={reflecting}
                    className="inline-flex items-center gap-2 rounded-full border border-[#C9A24B] text-[#C9A24B] px-4 py-2 font-ui text-xs hover:bg-[#C9A24B]/10 transition-colors disabled:opacity-50"
                  >
                    <Sparkles size={13} /> {reflecting ? "Le compagnon réfléchit…" : "Demander une réflexion au compagnon"}
                  </button>
                  {reflection && (
                    <p data-testid="reflect-output" className="mt-3 rounded-xl bg-[#C9A24B]/10 border border-[#C9A24B]/30 p-3 font-serif italic text-[#F6F2E9]/90 whitespace-pre-line">{reflection}</p>
                  )}
                </div>
              </div>
            )}

            <div className="mt-8">
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#F6F2E9]/50 text-center">Que choisissez-vous ?</p>
              <div className="grid gap-3 mt-4">
                {(scene.choices || []).filter((c) => !(c.hidden && !hasObject(c.requiredObject))).map((c) => {
                  const locked = !hasObject(c.requiredObject);
                  const secret = !!c.hidden;
                  return (
                    <button
                      key={c.key}
                      data-testid={`adventure-choice-${c.key}`}
                      onClick={() => makeChoice(c)}
                      disabled={locked}
                      className={`group text-left rounded-xl border p-4 transition-all ${locked ? "border-white/10 bg-white/5 opacity-60" : "border-[#C9A24B]/40 bg-white/5 hover:bg-[#C9A24B]/15 hover:border-[#C9A24B]"}`}
                    >
                      <span className="inline-flex items-center gap-3">
                        <span className="font-serif text-lg text-[#C9A24B]">{c.key}</span>
                        <span className="font-sans text-sm md:text-base text-[#F6F2E9]/90">{c.label}</span>
                      </span>
                      {locked && (
                        <span className="block mt-1 font-sans text-xs text-[#F6F2E9]/50">🔒 Nécessite : {c.requiredObject}</span>
                      )}
                      {secret && !locked && (
                        <span className="block mt-1 font-sans text-xs text-[#C9A24B]">✦ Passage secret</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {view === "freeEnd" && (
          <div data-testid="free-end-screen" className="mt-10 rounded-2xl border border-[#C9A24B]/50 bg-white/5 p-8 text-center">
            <h2 className="font-serif text-3xl md:text-4xl">L'aventure commence à peine…</h2>
            <p className="font-sans text-[#F6F2E9]/80 mt-4 max-w-md mx-auto leading-relaxed">
              Vous avez découvert les premières portes de l'Île de l'Abondance.
              Les 18 prochaines étapes vous attendent.
            </p>
            <button data-testid="free-end-continue" onClick={() => setView("lock")} className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-8 py-3.5 font-ui text-sm font-semibold hover:bg-[#b8923f] transition-colors">
              CONTINUER L'AVENTURE <ArrowRight size={16} />
            </button>
            <p className="font-sans text-xs text-[#F6F2E9]/50 mt-4 max-w-sm mx-auto">
              Accédez aux 21 étapes, aux embranchements, aux objets et aux différentes fins de l'aventure.
            </p>
          </div>
        )}

        {view === "lock" && (
          <LockScreen onUnlock={startCheckout} onPaypal={startPaypal} authenticated={authenticated} bookId={id} />
        )}

        {view === "ending" && scene && (
          <div data-testid="ending-screen" className="mt-8">
            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#C9A24B]/80 text-center">Fin de l'aventure</p>
            <h1 className="font-serif text-3xl md:text-4xl mt-2 text-center">{scene.title}</h1>
            <div className="gold-divider w-16 mx-auto my-5" />
            <p className="font-serif text-lg leading-relaxed text-[#F6F2E9]/90 text-center whitespace-pre-line">{scene.text}</p>

            <div data-testid="hero-bilan" className="mt-8 rounded-2xl border border-[#C9A24B]/40 bg-white/5 p-6">
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A24B] text-center">Votre aventure</p>
              <div className="gold-divider w-12 mx-auto my-4" />
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="font-ui text-[11px] tracking-widest text-[#F6F2E9]/50">VOTRE PARCOURS</p>
                  <p className="font-serif text-2xl mt-1">{(state.visited || []).length} étapes traversées</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(state.visited || []).map((v) => (
                      <span key={v} className="rounded-full bg-white/10 px-2 py-0.5 font-mono text-[10px] text-[#F6F2E9]/70">{v}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-ui text-[11px] tracking-widest text-[#F6F2E9]/50">VOTRE FIN</p>
                  <p data-testid="bilan-ending" className="font-serif text-3xl mt-1 text-[#C9A24B]">{ENDING_LABELS[scene.scene_id] || "Une fin"}</p>
                  <div className="grid grid-cols-4 gap-1.5 mt-3">
                    {C_META.map((c) => (
                      <div key={c.key} className="text-center rounded-lg bg-white/5 py-1.5">
                        <div className="font-sans text-[9px] tracking-wide" style={{ color: c.color }}>{c.label}</div>
                        <div className="font-serif text-lg">{state.scores?.[c.key] || 0}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="font-ui text-[11px] tracking-widest text-[#F6F2E9]/50 mt-6">VOTRE INVENTAIRE</p>
              <div className="flex flex-wrap gap-4 mt-3" data-testid="bilan-inventory">
                {Object.keys(OBJECT_IMAGES).filter((o) => (state.inventory || []).includes(o)).length === 0 ? (
                  <span className="font-sans text-xs text-[#F6F2E9]/40 italic">Aucun objet collecté sur ce chemin.</span>
                ) : (
                  Object.keys(OBJECT_IMAGES).filter((o) => (state.inventory || []).includes(o)).map((o) => (
                    <div key={o} className="text-center w-16">
                      <img src={OBJECT_IMAGES[o]} alt={o} className="h-14 w-14 mx-auto rounded-full object-cover ring-1 ring-[#C9A24B]/50" />
                      <span className="block font-sans text-[10px] text-[#F6F2E9]/80 mt-1">{o}</span>
                    </div>
                  ))
                )}
              </div>
              {(() => {
                const dom = C_META.reduce((a, b) => ((state.scores?.[b.key] || 0) > (state.scores?.[a.key] || 0) ? b : a), C_META[0]);
                return (
                  <p className="font-serif italic text-center text-[#F6F2E9]/85 mt-6">
                    Sur ce chemin, vous avez surtout nourri votre {dom.label.toLowerCase()}. {ENDING_CONCLUSIONS[scene.scene_id] || ""}
                  </p>
                );
              })()}
            </div>

            <div className="mt-8 rounded-2xl border border-[#C9A24B]/40 bg-white/5 p-6 text-center">
              <p className="font-sans text-[#F6F2E9]/80 leading-relaxed">
                Vous avez terminé votre première aventure. Mais vous n'avez découvert qu'un seul chemin.
                L'Île possède encore d'autres secrets. Voulez-vous recommencer ?
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <button data-testid="restart-button" onClick={restart} className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-6 py-3 font-ui text-sm font-semibold hover:bg-[#b8923f] transition-colors">
                  <RotateCcw size={16} /> RECOMMENCER
                </button>
                <button data-testid="see-journey" onClick={() => navigate("/mon-espace")} className="rounded-full border border-[#F6F2E9]/30 px-6 py-3 font-ui text-sm hover:bg-white/10 transition-colors">
                  VOIR MON PARCOURS
                </button>
                <Link to="/aventures" data-testid="other-books" className="rounded-full border border-[#F6F2E9]/30 px-6 py-3 font-ui text-sm hover:bg-white/10 transition-colors">
                  DÉCOUVRIR LES AUTRES LIVRES
                </Link>
              </div>
            </div>

            <div data-testid="bridge-aventure-jeu" className="mt-6 rounded-2xl border border-[#C9A24B]/30 bg-white/5 p-6 text-center">
              <p className="font-serif italic text-[#F6F2E9]/85 leading-relaxed">
                Votre aventure ne s'arrête pas ici. Explorez maintenant les 21 cartes de l'Île de l'Abondance.
              </p>
              <Link to="/jeu/ile" data-testid="bridge-to-jeu" className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm mt-4 hover:bg-[#2c4a2e] transition-colors">
                Découvrir le Jeu <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const ScoreBar = ({ scores }) => (
  <div data-testid="adventure-scores" className="mt-6 grid grid-cols-4 gap-2">
    {C_META.map((c) => (
      <div key={c.key} className="rounded-lg bg-white/5 p-2 text-center">
        <div className="font-sans text-[10px] tracking-wider" style={{ color: c.color }}>{c.label}</div>
        <div className="font-serif text-xl text-[#F6F2E9]">{scores?.[c.key] || 0}</div>
      </div>
    ))}
  </div>
);

const Inventory = ({ items }) => {
  const known = (items || []).filter((o) => OBJECT_IMAGES[o]);
  return (
    <div className="mt-4">
      <p className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.2em] text-[#F6F2E9]/50">
        <Backpack size={13} /> Mon sac d'aventure
      </p>
      <div data-testid="adventure-inventory" className="flex flex-wrap gap-2 mt-2 min-h-[36px] items-center">
        {known.length === 0 ? (
          <span className="font-sans text-xs text-[#F6F2E9]/40 italic">Votre sac est vide pour l'instant.</span>
        ) : (
          known.map((o) => (
            <span key={o} className="inline-flex items-center gap-1.5 rounded-full bg-[#C9A24B]/15 border border-[#C9A24B]/40 pl-1 pr-3 py-1 font-sans text-xs text-[#F6F2E9]">
              <img src={OBJECT_IMAGES[o]} alt={o} className="h-6 w-6 rounded-full object-cover ring-1 ring-[#C9A24B]/50" /> {o}
            </span>
          ))
        )}
      </div>
    </div>
  );
};

const LockScreen = ({ onUnlock, onPaypal, authenticated }) => (
  <div data-testid="lock-screen" className="mt-10 rounded-2xl border-2 border-[#C9A24B] bg-gradient-to-b from-white/10 to-transparent p-8 md:p-10 text-center">
    <Lock className="mx-auto text-[#C9A24B]" size={40} />
    <h2 className="font-serif text-3xl md:text-4xl mt-4">Une porte vient de se fermer…</h2>
    <p className="font-sans text-[#F6F2E9]/80 mt-4 max-w-lg mx-auto leading-relaxed">
      Vous avez atteint la limite de votre parcours découverte. Mais l'Île de l'Abondance possède
      encore de nombreux secrets. Derrière cette porte se trouvent 18 nouvelles étapes, de nouveaux
      choix, de nouveaux personnages et plusieurs fins possibles.
    </p>
    <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-1.5 max-w-md mx-auto font-sans text-sm text-[#F6F2E9]/85 text-left">
      {["21 étapes", "Plusieurs chemins", "Plusieurs fins", "Objets à collecter", "Progression des 4 C", "Carnet du héros", "Rejouabilité"].map((f) => (
        <li key={f} className="inline-flex items-center gap-2"><span className="text-[#C9A24B]">✓</span> {f}</li>
      ))}
    </ul>
    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
      <button data-testid="unlock-button" onClick={onUnlock} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm font-semibold hover:bg-[#b8923f] transition-colors">
        Carte bancaire — 19,90 €
      </button>
      <button data-testid="unlock-paypal" onClick={onPaypal} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#003087] text-white px-7 py-3.5 font-ui text-sm font-semibold hover:bg-[#00256a] transition-colors">
        Payer avec PayPal
      </button>
    </div>
    {!authenticated && (
      <p className="font-sans text-xs text-[#F6F2E9]/50 mt-3">Un compte vous sera demandé pour conserver votre accès et votre progression.</p>
    )}
  </div>
);
